import React from 'react';
import { useFormValidation } from '@/hooks/useFormValidation';
import { personalDataValidationRules, PersonalData } from './validations/PersonalData';

interface Customer {
  name: string;
  email: string;
  phoneNumber: string;
  document: {
    type: string;
    number: string;
    country: string;
  };
  address: {
    zipCode: string;
    street: string;
    streetNumber: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    country: string;
  };
}

interface PersonalDataFormProps {
  customer: Customer;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (customer: Customer) => void;
}

const PersonalDataForm: React.FC<PersonalDataFormProps> = ({
  customer,
  onInputChange,
  onSubmit
}) => {
  const { errors, validateFieldOnChange, isFormValid } = useFormValidation<PersonalData>(
    personalDataValidationRules
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const personalData = {
      name: customer.name,
      email: customer.email,
      phoneNumber: customer.phoneNumber,
      documentNumber: customer.document.number
    };
    
    if (isFormValid(personalData)) {
      onSubmit(customer);
    }
  };

  const handleInputChangeWithValidation = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Map field names for validation
    let fieldName = name;
    if (name === 'document.number') {
      fieldName = 'documentNumber';
    }
    validateFieldOnChange(fieldName as keyof PersonalData, value);
    
    onInputChange(e);
  };

  const currentPersonalData = {
    name: customer.name,
    email: customer.email,
    phoneNumber: customer.phoneNumber,
    documentNumber: customer.document.number
  };
  
  const formIsValid = isFormValid(currentPersonalData);
  return (
    <>
      <p className="text-sm text-gray-600 mb-4">Por favor, preencha seus dados pessoais.</p>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <div className="mb-3">
            <label htmlFor="name" className="block text-xs font-semibold text-gray-700 mb-1">Nome completo</label>
            <input
              id="name"
              type="text"
              name="name"
              value={customer.name}
              onChange={handleInputChangeWithValidation}
              required
              className={`input ${errors['name'] ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Nome completo"
            />
            {errors['name'] && (
              <p className="text-red-500 text-xs mt-1">{errors['name']}</p>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">E-mail</label>
            <input
              id="email"
              type="email"
              name="email"
              value={customer.email}
              onChange={handleInputChangeWithValidation}
              required
              className={`input ${errors['email'] ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="seu@email.com"
              disabled={!!customer.email} // Disable if email is already set
            />
            {errors['email'] && (
              <p className="text-red-500 text-xs mt-1">{errors['email']}</p>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="phoneNumber" className="block text-xs font-semibold text-gray-700 mb-1">Telefone</label>
            <input
              id="phoneNumber"
              type="tel"
              name="phoneNumber"
              value={customer.phoneNumber}
              onChange={handleInputChangeWithValidation}
              required
              className={`input ${errors['phoneNumber'] ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="(11) 99999-9999"
            />
            {errors['phoneNumber'] && (
              <p className="text-red-500 text-xs mt-1">{errors['phoneNumber']}</p>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="document.number" className="block text-xs font-semibold text-gray-700 mb-1">CPF</label>
            <input
              id="document.number"
              type="text"
              name="document.number"
              value={customer.document.number}
              onChange={handleInputChangeWithValidation}
              required
              className={`input ${errors['documentNumber'] ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="000.000.000-00"
            />
            {errors['documentNumber'] && (
              <p className="text-red-500 text-xs mt-1">{errors['documentNumber']}</p>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-3 mt-4 w-full">
          <button
            type="submit"
            disabled={!formIsValid}
            className={`w-full btn btn-primary ${!formIsValid ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Próximo
          </button>
        </div>
      </form>
    </>
  );
};

export default PersonalDataForm;
